/**
 * Avatar registry — single source of truth for avatar metadata.
 *
 * Includes:
 *   - `img`: portrait used in the HUD chip and themes page. Imported via
 *     webpack (not `/avatars/...`) so the bundler emits hashed asset URLs
 *     that resolve correctly on web AND inside Capacitor's Android WebView.
 *     Earlier we used `/avatars/runner.png` which works on the web preview
 *     but broke on the bundled AAB (white-circle placeholder reported by
 *     testers on v26).
 *   - `bonus`: the per-avatar passive perk shown in the unlock card and
 *     applied client-side (energy regen, hint regen, combo coin multiplier)
 *   - `winLine`: which coach voice clip to play on level clear, so each
 *     avatar feels like a different trainer (Boxer = aggressive, Yogi =
 *     calm focus, Runner = energetic encouragement, Athlete = beast mode)
 *
 * The default avatar is `runner` for new accounts.
 */
import runnerImg from "../assets/avatars/runner.png";
import yogiImg from "../assets/avatars/yogi.png";
import athleteImg from "../assets/avatars/athlete.png";

export const AVATAR_REGISTRY = {
  runner: {
    img: runnerImg,
    name: "Runner",
    // Energetic distance-runner — gets free energy a bit faster.
    bonus: { kind: "energy_regen", value: 0.2, label: "+20% faster energy refills" },
    winLine: "lets_go",
  },
  yogi: {
    img: yogiImg,
    name: "Yogi",
    // Calm focused practitioner — gets a free Coach Tip every day at 9am.
    bonus: { kind: "free_hint_daily", value: 1, label: "+1 free Coach Tip per day" },
    winLine: "focus",
  },
  athlete: {
    img: athleteImg,
    name: "Athlete",
    // Strength-trainer — combo bonus pays out a bit more.
    bonus: { kind: "combo_coin_bonus", value: 0.1, label: "+10% combo coin bonus" },
    winLine: "beast",
  },
};

export function avatarOf(profile) {
  return AVATAR_REGISTRY[profile?.selected_avatar] || AVATAR_REGISTRY.runner;
}

// Full URL for a user-uploaded custom profile photo, or null if none set.
// The backend stores a relative /api/... path; prepend the backend origin.
export function customAvatarSrc(profile) {
  const u = profile?.custom_avatar_url;
  if (!u) return null;
  if (u.startsWith("http")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
}

// Best avatar image for a leaderboard/podium row: custom upload if present,
// otherwise the selected preset avatar (falling back to Runner).
export function leaderAvatarSrc(row) {
  return (
    customAvatarSrc(row) ||
    (AVATAR_REGISTRY[row?.selected_avatar] || AVATAR_REGISTRY.runner).img
  );
}

// Map kind → profile key for the backend grant logic.
export const BONUS_TO_KEY = {
  energy_regen: "avatar_energy_regen_bonus",
  free_hint_daily: "avatar_free_hint",
  combo_coin_bonus: "avatar_combo_bonus",
};
